import { Gift, Trophy } from 'lucide-react'
import { KpiCard } from '@/components/profile/KpiCard'
import { ReferralCopyField } from '@/components/profile/referral/ReferralCopyField'
import { useGetProfileQuery } from '@/store/api/profileApi'
import { formatAmount } from '@/lib/format-number'
import { useAppSelector } from '@/store/hooks'
import { selectIsAuthenticated } from '@/store/slices/authSlice'

const REFERRAL_BANNER_IMAGE = '/images/profile/referral-banner.png'

function getPlayerSiteUrl() {
	return (import.meta.env.VITE_PLAYER_SITE_URL ?? '').replace(/\/$/, '')
}

function buildReferralLink(referralCode: string) {
	const siteUrl = getPlayerSiteUrl()

	if (!siteUrl || !referralCode) {
		return ''
	}

	return `${siteUrl}?code=${encodeURIComponent(referralCode)}`
}

export function ReferralPanel() {
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const { data: profileResponse } = useGetProfileQuery(undefined, { skip: !isAuthenticated })
	const profile = profileResponse?.data
	const referralCode = profile?.referral_code ?? ''
	const referralLink = buildReferralLink(referralCode)
	const totalRewards = (profile?.total_cpa_earned_amount ?? 0) + (profile?.total_commission_earned_amount ?? 0)
	const totalInvited = profile?.referral_count ?? 0

	return (
		<section className='flex flex-col gap-4'>
			<div className='relative mt-5 overflow-visible rounded-2xl border border-border bg-card shadow-[0_4px_12px_0_hsl(var(--background)/0.2)]'>
				<div className='absolute inset-0 overflow-hidden rounded-2xl' aria-hidden>
					<div className='absolute inset-0 bg-[linear-gradient(90deg,hsl(var(--card))_0%,hsl(var(--card)/0.92)_40%,hsl(var(--card)/0.42)_100%)]' />
				</div>
				<img
					src={REFERRAL_BANNER_IMAGE}
					alt=''
					className='pointer-events-none absolute -right-5 -top-9 z-0 h-[180px] w-[54%] object-contain object-right-top opacity-95 sm:-right-8 sm:-top-10 sm:h-[220px]'
					aria-hidden
				/>

				<div className='relative z-10 flex min-h-[190px] flex-col justify-end gap-5 overflow-hidden rounded-2xl p-5 sm:p-6'>
					<div className='relative z-10'>
						<h2 className='max-w-md text-2xl font-semibold leading-tight tracking-[-0.36px] text-foreground sm:text-3xl'>
							Invite friends and{' '}
							<span className='bg-gradient-to-r from-[hsl(var(--accent-green))] to-[hsl(var(--accent-blue))] bg-clip-text text-transparent'>earn 5%</span>
						</h2>
						<p className='mt-1 text-2xl font-semibold leading-tight tracking-[-0.36px] text-foreground sm:text-3xl'>from their winning bets</p>
					</div>

					<div className='relative z-20 rounded-xl bg-gradient-to-r from-[hsl(var(--accent-green))] to-[hsl(var(--accent-blue))] p-px shadow-[0_0_20px_0_hsl(var(--accent-green)/0.08)]'>
						<div className='overflow-hidden rounded-[10px] bg-secondary/95 backdrop-blur'>
							<div className='flex flex-col divide-y divide-border sm:flex-row sm:divide-x sm:divide-y-0'>
								<ReferralCopyField label='My Referral Link' value={referralLink} />
								<ReferralCopyField label='Referral Code' value={referralCode} />
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className='grid gap-4 sm:grid-cols-2'>
				<KpiCard
					title='Total Rewards'
					icon={Gift}
					gradientClassName='bg-gradient-to-b from-[hsl(var(--accent-green))] to-success'
					bannerTextClassName='text-background'
					value={`$ ${formatAmount(totalRewards)}`}
				/>
				<KpiCard
					title='Total Invited'
					icon={Trophy}
					gradientClassName='bg-gradient-to-b from-[hsl(var(--accent-blue))] to-info'
					bannerTextClassName='text-background'
					value={formatAmount(totalInvited, 0)}
				/>
			</div>
		</section>
	)
}
